import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import { apiUrls } from '../../../config/api/api-urls.config';
import { CollectionTranslation } from '../../../models/collections/collection-translation.model';
import { CollectionReward } from '../../../models/collections/collection-reward.model';
import { CollectionBoosterDrop } from '../../../models/collections/collection-booster-drop.model';
import { CollectionsLightResultsResponse } from '../../../models/collections/responses/api-collections-light-results-response.model';
import { CollectionsResultsResponse } from '../../../models/collections/responses/api-collections-results-response.model';
import { CollectionAlbumsResultsResponse } from '../../../models/collections/responses/api-collection-albums-results-response.model';
import { CollectionCardsResultsResponse } from '../../../models/collections/responses/api-collection-cards-results-response.model';
import { CollectionsPeriodsResultsResponse } from '../../../models/collections/responses/api-collections-periods-results-response.model';
import { CollectionsBoostersResultsResponse } from '../../../models/collections/responses/api-collections-boosters-results-response.model';

@Injectable({
  providedIn: 'root'
})
export class ApiCollectionsService {

  constructor(private apiService: ApiService) { }

  // --- Collections ---
  getAllCollections(): Promise<CollectionsLightResultsResponse> {
    return new Promise<CollectionsLightResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsGetAllCollectionsUrl, {}, CollectionsLightResultsResponse).then(
        (data: CollectionsLightResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  getCollections(page: number, resultCount: number): Promise<CollectionsResultsResponse> {
    return new Promise<CollectionsResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsGetCollectionsUrl, { page, result_count: resultCount, filters: {} }, CollectionsResultsResponse).then(
        (data: CollectionsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addCollection(name: string, translations: CollectionTranslation[], rewards: CollectionReward[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { name, translations: this.mapTranslations(translations), rewards: this.mapRewards(rewards) };
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsAddCollectionUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editCollection(collectionId: number, name: string, translations: CollectionTranslation[], rewards: CollectionReward[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { collection_id: collectionId, name, translations: this.mapTranslations(translations), rewards: this.mapRewards(rewards) };
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsEditCollectionUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteCollection(collectionId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsDeleteCollectionUrl, { collection_id: collectionId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  // --- Albums ---
  getCollectionAlbums(collectionId: number, page: number, resultCount: number): Promise<CollectionAlbumsResultsResponse> {
    return new Promise<CollectionAlbumsResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsGetCollectionAlbumsUrl, { collection_id: collectionId, page, result_count: resultCount, filters: {} }, CollectionAlbumsResultsResponse).then(
        (data: CollectionAlbumsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addCollectionAlbum(collectionId: number, name: string, translations: CollectionTranslation[], rewards: CollectionReward[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { collection_id: collectionId, name, translations: this.mapTranslations(translations), rewards: this.mapRewards(rewards) };
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsAddCollectionAlbumUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editCollectionAlbum(albumId: number, collectionId: number, name: string, translations: CollectionTranslation[], rewards: CollectionReward[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { album_id: albumId, collection_id: collectionId, name, translations: this.mapTranslations(translations), rewards: this.mapRewards(rewards) };
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsEditCollectionAlbumUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteCollectionAlbum(albumId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsDeleteCollectionAlbumUrl, { album_id: albumId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  // --- Cards ---
  getCollectionAlbumCards(albumId: number, page: number, resultCount: number): Promise<CollectionCardsResultsResponse> {
    return new Promise<CollectionCardsResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsGetCollectionAlbumCardsUrl, { album_id: albumId, page, result_count: resultCount, filters: {} }, CollectionCardsResultsResponse).then(
        (data: CollectionCardsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addCollectionAlbumCard(albumId: number, name: string, rankType: number, translations: CollectionTranslation[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { album_id: albumId, name, rank_type: rankType, translations: this.mapTranslations(translations) };
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsAddCollectionAlbumCardUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editCollectionAlbumCard(cardId: number, albumId: number, name: string, rankType: number, translations: CollectionTranslation[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { card_id: cardId, album_id: albumId, name, rank_type: rankType, translations: this.mapTranslations(translations) };
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsEditCollectionAlbumCardUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteCollectionAlbumCard(cardId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsDeleteCollectionAlbumCardUrl, { card_id: cardId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  // --- Periods ---
  getCollectionsPeriods(page: number, resultCount: number): Promise<CollectionsPeriodsResultsResponse> {
    return new Promise<CollectionsPeriodsResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsGetCollectionsPeriodsUrl, { page, result_count: resultCount, filters: {} }, CollectionsPeriodsResultsResponse).then(
        (data: CollectionsPeriodsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addCollectionPeriod(collectionId: number | null, name: string, startDateTimestamp: number, endDateTimestamp: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { collection_id: collectionId, name, start_date_timestamp: startDateTimestamp, end_date_timestamp: endDateTimestamp };
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsAddCollectionPeriodUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editCollectionPeriod(collectionPeriodId: number, collectionId: number | null, name: string, startDateTimestamp: number, endDateTimestamp: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { collection_period_id: collectionPeriodId, collection_id: collectionId, name, start_date_timestamp: startDateTimestamp, end_date_timestamp: endDateTimestamp };
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsEditCollectionPeriodUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteCollectionPeriod(collectionPeriodId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsDeleteCollectionPeriodUrl, { collection_period_id: collectionPeriodId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  // --- Boosters ---
  getCollectionsBoosters(page: number, resultCount: number): Promise<CollectionsBoostersResultsResponse> {
    return new Promise<CollectionsBoostersResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsGetCollectionsBoostersUrl, { page, result_count: resultCount, filters: {} }, CollectionsBoostersResultsResponse).then(
        (data: CollectionsBoostersResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  editCollectionBooster(boosterRankType: number, cardsDropCount: number, drops: CollectionBoosterDrop[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = {
        booster_rank_type: boosterRankType,
        cards_drop_count: cardsDropCount,
        drops: drops.map(d => ({ card_rank_type: d.cardRankType, rarity_weight: d.rarityWeight }))
      };
      this.apiService.launchAdminApiRequestPost(apiUrls.collectionsEditCollectionBoosterUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  // --- Images ---
  uploadCollectionImage(collectionId: number, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('collection_id', String(collectionId));
    return this.apiService.launchAdminApiRequestPostFile(apiUrls.collectionsUploadCollectionImageUrl, formData);
  }

  uploadCollectionAlbumImage(albumId: number, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('album_id', String(albumId));
    return this.apiService.launchAdminApiRequestPostFile(apiUrls.collectionsUploadCollectionAlbumImageUrl, formData);
  }

  uploadCollectionAlbumCardImage(cardId: number, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('card_id', String(cardId));
    return this.apiService.launchAdminApiRequestPostFile(apiUrls.collectionsUploadCollectionAlbumCardImageUrl, formData);
  }

  private mapTranslations(translations: CollectionTranslation[]): object[] {
    return translations.map(t => ({ language_id: t.languageId, name: t.name }));
  }

  private mapRewards(rewards: CollectionReward[]): object[] {
    return rewards.map(r => ({
      ...(r.rewardId > 0 ? { reward_id: r.rewardId } : {}),
      currency_type: r.currencyType,
      currency_quantity: r.currencyQuantity,
      currency_ticket_cost: r.currencyTicketCost
    }));
  }
}
